/**
 * @module app.cm
 */

import { autoinject, bindingMode } from 'aurelia-framework';
import { ComponentBase, IForm } from 'app/shared/lib/essette-site';
import { bindable } from 'aurelia-typed-observable-plugin';
import { ContactLogService } from 'app/core/services/contact-log-service';
import { apiCall } from 'app/shared/lib/essette-api';
import { Toaster } from 'app/shared/lib/toaster';
import { EventAggregator } from 'aurelia-event-aggregator';
import { ContactLogMethodReloadEvent } from 'app/cm/lib/events';
import { IContactLogConfig } from 'app/core/models/essette-interfaces';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { forceNull } from 'app/shared/lib/utilities';
import * as CM from 'app/cm/models/essette-api';

@autoinject()
export class SaveOutcome extends ComponentBase implements IForm {

    ////****************** Fields and Properties ************************************////
    @bindable.object({ defaultBindingMode: bindingMode.twoWay, changeHandler: 'reload' })
    public outcome: CM.CaseContactLogOutcome = {};
    protected outcomeId: string | null = null;

    @bindable
    public config: IContactLogConfig = {};

    protected selectedContactLogMethod: CM.CaseContactLogMethod | null = null;
    protected contactLogMethods: ReadonlyArray<CM.CaseContactLogMethod> | null = null;

    protected contactOutcomeText: string = '';
    protected contactOutcomeCodeText: string = '';
    protected contactOutcomeSuccess: boolean = false;

    protected originalOutcome: CM.CaseContactLogOutcome = {};

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly service: ContactLogService,
        private readonly toaster: Toaster,
        private readonly events: EventAggregator,
        ...rest: any[]) {
        super(...rest);
    }

    protected bind(): void {
        if(!this.config) { throw new Error('Bindable property [config] was not set'); }

        this.service.configure(this.config);
    }

    ////****************** Events ***************************************************////

    ////****************** Functions ************************************************////
    private async reload(): Promise<void> {
        await this.loadLookups();
        if(!this.outcome) return;

        // Params for outcome info
        this.outcomeId = this.outcome.id || null
        this.selectedContactLogMethod = this.outcome.method || null;
        this.contactOutcomeText = this.outcome.description || '';
        this.contactOutcomeCodeText = this.outcome.code || '';
        this.contactOutcomeSuccess = this.outcome.flag?.isSuccessful || false;
        this.originalOutcome = this.buildOutcome();
    }

    protected buildOutcome(): CM.CaseContactLogOutcome {
        const saveOutcome: CM.CaseContactLogOutcome = {
            id: this.outcome.id,
            description: this.contactOutcomeText,
            code: forceNull(this.contactOutcomeCodeText),
            method: { id: this.selectedContactLogMethod?.id },
            flag: { isSuccessful: this.contactOutcomeSuccess }
        };

        return saveOutcome;
    }

    ////****************** IForm ****************************************************////
    public hasChanges(): boolean {
        throw new Error('Method not implemented.');
    }

    public async submit(): Promise<boolean> {
        const isSuccess = await this.saveOutcome();
        if(isSuccess) this.events.publish(new ContactLogMethodReloadEvent());
        return isSuccess;
    }

    public reset(): void {
        this.selectedContactLogMethod = this.originalOutcome.method || {};
        this.contactOutcomeSuccess = this.originalOutcome.flag?.isSuccessful || false;
        this.contactOutcomeText = this.originalOutcome.description || '';
        this.contactOutcomeCodeText = this.originalOutcome.code || '';
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadLookups(): Promise<void> {
        const options: Partial<IQueryLoadOptions> = {
            all: true,
            includeInactive: !!this.outcome.id // if outcome has an id, include inactive methods
        };

        const fields = ['description'];

        try {
            this.state.beginLoad();
            const response = await this.service.loadContactLogMethods(options, undefined, undefined, fields);
            this.contactLogMethods = response.results;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    protected async saveOutcome(): Promise<boolean> {
        const insertOutcome: CM.CaseContactLogOutcome = this.buildOutcome();

        let isSuccessful = false;
        try {
            this.state.beginSave();
            const response = await this.service.saveContactLogOutcome(insertOutcome);
            isSuccessful = response.isSuccessful;

            return isSuccessful;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.toaster.standard.saved('core:contactLog.outcome.$long', isSuccessful);
            this.state.endSave();
        }
    }
}
